import React from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Flex,
    Heading,
    Img,
    Skeleton,
    SkeletonText,
    SimpleGrid,
    Text,
    useMultiStyleConfig,
    Box,
} from '@chakra-ui/react'
// import {mockData} from './mockData'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {ChefIcon, CareShieldIcon} from '../icons'
import {useIntl} from 'react-intl'
import {remCalc} from '../../theme/_utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useProductRegistrationHandler from '../../custom-hooks/use-product-registration-handler'
import routingObject from '../../utils/static-routes'
import useSlideUpAnimation, {useAnimRef} from '../../custom-hooks/use-slide-up-animation'
import {useGetSidecard} from '../../custom-api/hooks/use-get-sidecard'

const SideCardPDP = ({product, buttonVariant = false, useProductFields = false}) => {
    const styles = useMultiStyleConfig('SideCardPDP', {
        variant: useProductFields ? 'productFields' : buttonVariant ? 'button' : 'default',
    })
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const {containerRef} = useSlideUpAnimation()
    const {ref: imgRef, getAnimProps: getImgAnimProps} = useAnimRef()
    const {ref: gridRef, getAnimProps: getGridAnimProps} = useAnimRef()

    // Fetch sidecard data from Contentstack
    const {data: sidecardData, isLoading: isSidecardLoading} = useGetSidecard()

    // const mockProduct = mockData.product

    let productRegistrationLink = null
    if (product?.id) {
        productRegistrationLink = `${routingObject.PRODUCT_REGISTRATION_PATH}/select?text=${product?.id}`
    }
    const handleProductRegistrationClick = useProductRegistrationHandler(
        routingObject,
        productRegistrationLink,
    )

    // Handle product fields variant
    if (useProductFields && !product?.c_sidecardTitle) {
        return null
    }

    const title = buttonVariant
        ? formatMessage({
              id: 'side_card_pdp_manuals.labels.title',
              defaultMessage: 'Manuals & Product Registration',
          })
        : useProductFields
        ? product?.c_sidecardTitle
        : sidecardData?.title || product?.c_sidecard?.title

    // Return null if there's no content to display
    if (!buttonVariant && !title) {
        if (!useProductFields && isSidecardLoading) {
            return (
                <Flex
                    {...styles.container}
                    data-testid="side-card-pdp-loading"
                    aria-label={formatMessage({
                        id: 'side_card_pdp.loading',
                        defaultMessage: 'Loading product benefits',
                    })}
                >
                    <Skeleton {...styles.image} borderRadius="xl" />
                    <Flex {...styles.contentContainer}>
                        <Skeleton height={remCalc(28)} width="72%" borderRadius="full" />
                        <SkeletonText noOfLines={2} spacing="2" skeletonHeight={remCalc(10)} />
                        <SimpleGrid {...styles.sectionsContainer} gap={3}>
                            <Flex align="center" gap={3}>
                                <Skeleton boxSize={remCalc(24)} borderRadius="full" />
                                <Flex flex="1" direction="column" gap={1}>
                                    <Skeleton
                                        height={remCalc(12)}
                                        width="78%"
                                        borderRadius="full"
                                    />
                                    <Skeleton
                                        height={remCalc(10)}
                                        width="88%"
                                        borderRadius="full"
                                    />
                                </Flex>
                                <Skeleton boxSize={remCalc(16)} borderRadius="md" />
                            </Flex>
                            <Flex align="center" gap={3}>
                                <Skeleton boxSize={remCalc(24)} borderRadius="full" />
                                <Flex flex="1" direction="column" gap={1}>
                                    <Skeleton
                                        height={remCalc(12)}
                                        width="70%"
                                        borderRadius="full"
                                    />
                                    <Skeleton
                                        height={remCalc(10)}
                                        width="82%"
                                        borderRadius="full"
                                    />
                                </Flex>
                                <Skeleton boxSize={remCalc(16)} borderRadius="md" />
                            </Flex>
                            <Flex align="center" gap={3}>
                                <Skeleton boxSize={remCalc(24)} borderRadius="full" />
                                <Flex flex="1" direction="column" gap={1}>
                                    <Skeleton
                                        height={remCalc(12)}
                                        width="74%"
                                        borderRadius="full"
                                    />
                                    <Skeleton
                                        height={remCalc(10)}
                                        width="86%"
                                        borderRadius="full"
                                    />
                                </Flex>
                                <Skeleton boxSize={remCalc(16)} borderRadius="md" />
                            </Flex>
                        </SimpleGrid>
                    </Flex>
                </Flex>
            )
        }

        return null
    }

    const bodyCopy = buttonVariant
        ? formatMessage({
              id: 'side_card_pdp_manuals.labels.subtitle',
              defaultMessage: 'Manuals & Product Registration Subtitle',
          })
        : useProductFields
        ? product?.c_sidecardSubtitle
        : sidecardData?.bodyCopy || product?.c_sidecard?.bodyCopy

    const sidecardImage =
        useProductFields && product?.c_sidecardImage
            ? {url: `https://dam.delonghi.com/${product.c_sidecardImage}`}
            : sidecardData?.image
            ? {url: sidecardData.image}
            : {url: getAssetUrl('static/img/404Images/404Image-delonghi.png')}

    // Build sections array
    let sidecardSections = null
    if (useProductFields) {
        // Use product fields: c_sidecardEditorialSection1-4
        // These can be either HTML strings or objects with title/subtitle
        const sections = [
            product?.c_sidecardEditorialSection1,
            product?.c_sidecardEditorialSection2,
            product?.c_sidecardEditorialSection3,
            product?.c_sidecardEditorialSection4,
        ].filter(Boolean) // Remove null/undefined
        sidecardSections = sections.length > 0 ? sections : null
    } else {
        sidecardSections = buttonVariant
            ? null
            : sidecardData?.sections || product?.c_sidecard?.sections
    }

    return (
        <Flex {...styles.container} ref={containerRef} data-testid="side-card-pdp-container">
            <Img
                src={sidecardImage.url}
                alt="Benefits"
                {...styles.image}
                ref={imgRef}
                {...getImgAnimProps(0)}
            />

            <Flex {...styles.contentContainer}>
                <Flex {...styles.headerContainer}>
                    <Heading {...styles.title}>{title}</Heading>
                    <Text {...styles.description}>{bodyCopy}</Text>
                </Flex>

                <SimpleGrid ref={gridRef} {...styles.sectionsContainer}>
                    {buttonVariant ? (
                        <>
                            <Flex {...styles.sectionItem} {...getGridAnimProps(1)}>
                                <Flex {...styles.sectionTextContainer}>
                                    <Text {...styles.sectionTitle}>
                                        {formatMessage({
                                            defaultMessage: 'Instruction Manual',
                                            id: 'product_detail.product_documentation.manual_title',
                                        })}
                                    </Text>
                                    <Text {...styles.sectionSubtitle}>
                                        {formatMessage({
                                            defaultMessage: 'Instruction Manual Subtitle',
                                            id: 'product_detail.product_documentation.manual_subtitle',
                                        })}
                                    </Text>
                                </Flex>

                                <Button
                                    variant={'outline-not-rounded'}
                                    size={'large'}
                                    onClick={() =>
                                        product?.c_subModelIdentifier &&
                                        navigate(`/s/${product.c_subModelIdentifier}`)
                                    }
                                    {...styles.sectionButton}
                                >
                                    {formatMessage({
                                        defaultMessage: 'Visit Instruction Manual',
                                        id: 'product_detail.product_documentation.manual_title',
                                    })}
                                </Button>
                            </Flex>

                            <Flex {...styles.sectionItem} {...getGridAnimProps(2)}>
                                <Flex {...styles.sectionTextContainer}>
                                    <Text {...styles.sectionTitle}>
                                        {formatMessage({
                                            defaultMessage: 'Register your product',
                                            id: 'product_detail.product_documentation.register_title',
                                        })}
                                    </Text>
                                    <Text {...styles.sectionSubtitle}>
                                        {formatMessage({
                                            defaultMessage: 'Register your product subtitle',
                                            id: 'product_detail.product_documentation.register_subtitle',
                                        })}
                                    </Text>
                                </Flex>

                                <Button
                                    variant={'outline-not-rounded'}
                                    size={'large'}
                                    onClick={() => handleProductRegistrationClick()}
                                    {...styles.sectionButton}
                                >
                                    {formatMessage({
                                        defaultMessage: 'Register your product',
                                        id: 'product_detail.product_documentation.register_title',
                                    })}
                                </Button>
                            </Flex>
                        </>
                    ) : (
                        <>
                            {sidecardSections?.map((section, index) =>
                                typeof section === 'string' ? (
                                    // Handle HTML strings from product fields
                                    <Flex
                                        key={index}
                                        {...styles.sectionItem}
                                        {...getGridAnimProps(index)}
                                    >
                                        <CareShieldIcon {...styles.sectionItemHtmlIcon} />
                                        <Box
                                            dangerouslySetInnerHTML={{__html: section}}
                                            sx={styles.sectionItemHtml}
                                        />
                                    </Flex>
                                ) : (
                                    // Handle objects with title/subtitle from Contentstack
                                    <Flex
                                        key={section.id}
                                        {...styles.sectionItem}
                                        {...getGridAnimProps(index)}
                                    >
                                        <ChefIcon {...styles.sectionIcon} />
                                        <Flex {...styles.sectionTextContainer}>
                                            <Text {...styles.sectionTitle}>{section.title}</Text>
                                            <Text {...styles.sectionSubtitle}>
                                                {section.subtitle}
                                            </Text>
                                        </Flex>
                                    </Flex>
                                ),
                            )}
                        </>
                    )}
                </SimpleGrid>
            </Flex>
        </Flex>
    )
}

export default SideCardPDP

SideCardPDP.propTypes = {
    product: PropTypes.object,
    buttonVariant: PropTypes.bool,
    useProductFields: PropTypes.bool,
}
